/**
 * SolCalc Core Types
 *
 * This file defines all types used throughout the core engine.
 * These types are the contract between parser, evaluator, and consumers.
 */
// ============================================================================
// Token Types (Lexer Output)
// ============================================================================
export var TokenType;
(function (TokenType) {
    TokenType["NUMBER"] = "NUMBER";
    TokenType["DECIMAL_LITERAL"] = "DECIMAL_LITERAL";
    TokenType["IDENTIFIER"] = "IDENTIFIER";
    TokenType["TYPE_BOUND"] = "TYPE_BOUND";
    TokenType["PLUS"] = "PLUS";
    TokenType["MINUS"] = "MINUS";
    TokenType["MULTIPLY"] = "MULTIPLY";
    TokenType["DIVIDE"] = "DIVIDE";
    TokenType["POWER"] = "POWER";
    TokenType["LPAREN"] = "LPAREN";
    TokenType["RPAREN"] = "RPAREN";
    TokenType["EQUAL"] = "EQUAL";
    TokenType["NOT_EQUAL"] = "NOT_EQUAL";
    TokenType["LESS_THAN"] = "LESS_THAN";
    TokenType["LESS_THAN_OR_EQUAL"] = "LESS_THAN_OR_EQUAL";
    TokenType["GREATER_THAN"] = "GREATER_THAN";
    TokenType["GREATER_THAN_OR_EQUAL"] = "GREATER_THAN_OR_EQUAL";
    TokenType["EOF"] = "EOF";
})(TokenType || (TokenType = {}));
// ============================================================================
// AST Node Types (Parser Output)
// ============================================================================
export var ASTNodeType;
(function (ASTNodeType) {
    ASTNodeType["NUMBER_LITERAL"] = "NUMBER_LITERAL";
    ASTNodeType["TYPE_BOUND_LITERAL"] = "TYPE_BOUND_LITERAL";
    ASTNodeType["IDENTIFIER"] = "IDENTIFIER";
    ASTNodeType["BINARY_OP"] = "BINARY_OP";
    ASTNodeType["EXPONENTIATION"] = "EXPONENTIATION";
    ASTNodeType["COMPARISON"] = "COMPARISON";
})(ASTNodeType || (ASTNodeType = {}));
// ============================================================================
// Error Types
// ============================================================================
export class SolCalcError extends Error {
    constructor(message) {
        super(message);
        this.name = 'SolCalcError';
    }
}
export class ParseError extends SolCalcError {
    constructor(message, position) {
        super(message);
        this.position = position;
        this.name = 'ParseError';
    }
}
export class EvaluationError extends SolCalcError {
    constructor(message) {
        super(message);
        this.name = 'EvaluationError';
    }
}
export class DecimalMismatchError extends EvaluationError {
    constructor(leftDecimals, rightDecimals, operator) {
        super(`Decimal mismatch in ${operator}: left has ${leftDecimals} decimals, right has ${rightDecimals} decimals. ` +
            `Addition and subtraction require matching decimals.`);
        this.leftDecimals = leftDecimals;
        this.rightDecimals = rightDecimals;
        this.operator = operator;
        this.name = 'DecimalMismatchError';
    }
}
export class InvalidExponentiationError extends EvaluationError {
    constructor(message) {
        super(`Invalid exponentiation: ${message}. Only \`10 ** n\` with dimensionless integer n is supported.`);
        this.name = 'InvalidExponentiationError';
    }
}
export class DivisionByZeroError extends EvaluationError {
    constructor() {
        super('Division by zero');
        this.name = 'DivisionByZeroError';
    }
}
export class UndefinedVariableError extends EvaluationError {
    constructor(variableName) {
        super(`Variable "${variableName}" is not defined. All variables must have explicit values and decimals.`);
        this.variableName = variableName;
        this.name = 'UndefinedVariableError';
    }
}
export class MissingDecimalsError extends EvaluationError {
    constructor(variableName) {
        super(`Variable "${variableName}" is missing decimals. All variables must have explicit decimals >= 0.`);
        this.variableName = variableName;
        this.name = 'MissingDecimalsError';
    }
}
export class ComparisonError extends EvaluationError {
    constructor(message) {
        super(message);
        this.name = 'ComparisonError';
    }
}
export class ComparisonDecimalMismatchError extends ComparisonError {
    constructor(leftDecimals, rightDecimals) {
        super(`Cannot compare values with different decimals (${leftDecimals} vs ${rightDecimals}). ` +
            `Normalize decimals explicitly before comparison.`);
        this.leftDecimals = leftDecimals;
        this.rightDecimals = rightDecimals;
        this.name = 'ComparisonDecimalMismatchError';
    }
}
//# sourceMappingURL=types.js.map